// CampaignFormApplications.js (güncel) — yalnızca gerekli eklemeler yapıldı

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Services from 'src/services/Services';
import {
  Button, Modal, Box, Typography, IconButton, Chip, Card, Divider,
  TextField, Checkbox, RadioGroup, FormControlLabel, Radio, Switch,
  FormControl, Select, MenuItem, Stack, Tooltip,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import campaignStatusList from 'src/data/campaignStatusList.json';
import DynamicTable from 'src/components/shared/DynamicTable';
import ExportToExcelButton from 'src/components/shared/ExportToExcelButton';
import api from 'src/services/BaseService';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Iconify from 'src/components/iconify';

/** Basit kart sarmalayıcı */
function QuestionCard({ children }) {
  return <div>{children}</div>;
}

const DAY_TR = {
  monday: 'Pazartesi',
  tuesday: 'Salı',
  wednesday: 'Çarşamba',
  thursday: 'Perşembe',
  friday: 'Cuma',
  saturday: 'Cumartesi',
  sunday: 'Pazar',
};

/** ------- Drag & Drop yardımcıları (sadece sıralama için) ------- */
function useDragList(initial) {
  const [list, setList] = useState(initial);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => setList(initial), [initial]);

  const handleDragStart = (index) => () => { dragItem.current = index; };
  const handleDragEnter = (index) => () => { dragOverItem.current = index; };
  const handleDragEnd = () => {
    const from = dragItem.current;
    const to = dragOverItem.current;
    if (from === null || to === null || from === to) {
      dragItem.current = null; dragOverItem.current = null; return;
    }
    setList(prev => {
      const arr = [...prev];
      const item = arr.splice(from, 1)[0];
      arr.splice(to, 0, item);
      return arr;
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return { list, setList, handleDragStart, handleDragEnter, handleDragEnd };
}

function normalizeTR(s) {
  return (s ?? '')
    .toString()
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    // unicode diyakritik temizleme (ğ/ü/ş vs. için görsel uyum)
    .replace(/\p{Diacritic}/gu, '');
}
/** ------- Kolon Düzenleme Modalı ------- */
function ColumnEditorModal({
  open,
  onClose,
  allColumns,         // {id,label}[]  (actions hariç)
  visibleOrder,       // string[]       (actions hariç)
  onApply,            // (newOrder:string[]) => void
}) {
  // actions sabit - modalda hiç görünmüyor
  const [visible, setVisible] = useState(visibleOrder);
  const allSet = useMemo(() => new Set(allColumns.map(c => c.id)), [allColumns]);

  // 🔎 Yeni: arama metni
  const [query, setQuery] = useState('');

  useEffect(() => setVisible(visibleOrder.filter(id => allSet.has(id))), [visibleOrder, allSet]);

  const hidden = useMemo(
    () => allColumns.map(c => c.id).filter(id => !visible.includes(id)),
    [allColumns, visible]
  );

  const visibleItems = useMemo(
    () => visible.map(id => allColumns.find(c => c.id === id)).filter(Boolean),
    [visible, allColumns]
  );
  const hiddenItems = useMemo(
    () => hidden.map(id => allColumns.find(c => c.id === id)).filter(Boolean),
    [hidden, allColumns]
  );

  const {
    list: dragList,
    setList: setDragList,
    handleDragStart,
    handleDragEnter,
    handleDragEnd
  } = useDragList(visibleItems);

  useEffect(() => {
    // allColumns değişirse görünür listedeki objeleri güncelle
    setDragList(visible.map(id => allColumns.find(c => c.id === id)).filter(Boolean));
  }, [visible, allColumns, setDragList]);

  const apply = () => {
    const newOrder = dragList.map(c => c.id);
    onApply(newOrder);
    onClose();
  };

  const removeFromVisible = (id) => setVisible(prev => prev.filter(x => x !== id));
  const addToVisible = (id) => setVisible(prev => [...prev, id]);

  // 🔎 Yeni: filtreleme fonksiyonu
  const matches = (label) => normalizeTR(label).includes(normalizeTR(query));

  // 🔎 Yeni: filtrelenmiş listeler (sadece render için; state aynı kalır)
  const filteredVisibleList = useMemo(
    () => (query ? dragList.filter(c => matches(c.label)) : dragList),
    [dragList, query]
  );

  const filteredHiddenList = useMemo(
    () => (query ? hiddenItems.filter(c => matches(c.label)) : hiddenItems),
    [hiddenItems, query]
  );

  // Filtre esnasında sürükle-bırak devre dışı: indeks uyuşmazlıklarını önler
  const dndDisabled = query.length > 0;

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: { xs: '95%', sm: 900 }, bgcolor: '#fff', borderRadius: 2, boxShadow: 24, p: 3,
          maxHeight: '85vh', overflow: 'auto'
        }}
      >
        <Box position="relative" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 800, textAlign: 'center' }}>
            Tablo Düzenle
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Kapat"
            sx={{ position: 'absolute', top: -4, right: -4 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography sx={{ mb: 1.5, color: '#6B7280', fontWeight: '500', fontSize: '14px', fontFamily: 'Inter' }}>
          Kolonları arayın, ekleyin/çıkarın ve sıralarını değiştirin
        </Typography>

        {/* 🔎 Yeni: Global arama alanı (label içinde) */}
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Kolon ara... (örn: telefon, email, il, ilçe)"
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
          }}
        >
          {/* Görünür Kolonlar */}
          <Box
            sx={{
              borderTop: '1px solid #EFEFEF', borderBottom: '1px solid #EFEFEF', borderRight: '1px solid #EFEFEF', p: 1.5, minHeight: 380
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <VisibilityOutlinedIcon style={{ color: '#16A34A' }} />
              <Typography sx={{ fontWeight: 600, color: '#667085', fontSize: 14 }}>
                Görünür Kolonlar ({dragList.length})
              </Typography>
              {dndDisabled && (
                <Typography sx={{ ml: 'auto', fontSize: 11, color: '#9CA3AF' }}>
                  Filtre aktifken sürükle-bırak kapalı
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 430, overflow: 'auto' }}>
              {filteredVisibleList.map((col, idx) => (
                <Box
                  key={col.id}
                  draggable={!dndDisabled}
                  onDragStart={!dndDisabled ? handleDragStart(idx) : undefined}
                  onDragEnter={!dndDisabled ? handleDragEnter(idx) : undefined}
                  onDragEnd={!dndDisabled ? handleDragEnd : undefined}
                  sx={{
                    height: '60px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    border: '2px solid #BBF7D0', bgcolor: '#F0FDF4', borderRadius: 1.5, p: 1,
                    opacity: filteredVisibleList.length ? 1 : 0.8
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.25}>
                    <DragIndicatorIcon sx={{ color: dndDisabled ? '#D1D5DB' : '#9CA3AF' }} />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{col.label}</Typography>
                      <Typography sx={{ color: '#9CA3AF', fontSize: 11 }}>
                        #{dragList.findIndex(x => x.id === col.id) + 1}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => removeFromVisible(col.id)}>
                    <ClearIcon sx={{ color: '#DC2626' }} fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Gizli Kolonlar */}
          <Box
            sx={{
              borderTop: '1px solid #EFEFEF', borderBottom: '1px solid #EFEFEF', p: 1.5, minHeight: 380
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>

              <VisibilityOffOutlinedIcon />
              <Typography sx={{ fontWeight: 600, color: '#667085', fontSize: 14 }}>
                Gizli Kolonlar ({hiddenItems.length})
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 430, overflow: 'auto' }}>
              {filteredHiddenList.map((col) => (
                <Box
                  key={col.id}
                  sx={{
                    minHeight: '60px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    border: '2px solid #E5E7EB', bgcolor: '#F9FAFB', borderRadius: 1.5, p: 1
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{col.label}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => addToVisible(col.id)}>
                    <AddIcon style={{ color: '#16A34A' }} fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box mt={3} display="flex" justifyContent="flex-end" gap={1.5}>
          <Button variant="contained" onClick={apply}>Kaydet</Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default function CampaignFormApplications() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  // === Eklemeler (component gövdesine) ===
  const [showPopUpError, setShowPopUpError] = useState(false);


  // Sorular
  const [questions, setQuestions] = useState([]); // [{questionID, content:{label,type,options,labels}}]

  // Detay modal state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null); // ham başvuru objesi
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Kolon düzenleme
  const [columnEditorOpen, setColumnEditorOpen] = useState(false);
  // actions sabit olduğundan burada tutulmaz. Varsayılan görünür kolon listesi:
  const [visibleColumnIds, setVisibleColumnIds] = useState([
    'campaignTitle',
    'applicantName',
    'phone',
    'email',
    'createdDateTime',
    'status',
  ]);

  const formatDate = (unixSeconds) => {
    if (!unixSeconds) return '-';
    const d = new Date(Number(unixSeconds) * 1000);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }).format(d);
  };

  // ================================================================

  const getCampaignQuestions = () => {
    Services.GetCampaignQuestions(parsedUser.token)
      .then((res) => {
        const arr = Array.isArray(res?.data?.result) ? res.data.result : [];
        setQuestions(arr);
      })
      .catch((err) => console.log(err));
  };

  const fetchCampaignApplications = async () => {
    try {
      const res = await Services.GetSubmittedCampaignForms(parsedUser.token);
      const list = Array.isArray(res?.data?.result) ? res.data.result : [];
      return list.map((it) => ({
        id: it.id,
        campaignTitle: it?.dataJSON?.campaignTitle ?? '-',
        createdDateTime: it?.createdDateTime ?? null,
        status: it?.status ?? '-',
        sourceType: 'Kampanya',
        __raw: it
      }));
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const fetchContactFormResults = async () => {
    try {
      const res = await Services.GetContactFormResults(parsedUser.token);
      console.log(res.data.result)
      const arr = Array.isArray(res?.data?.result) ? res.data.result : [];
      return arr.map((it) => ({
        id: it.id, // istersen çakışma riskine karşı `id: `contact_${it.id}` yapabilirsin
        campaignTitle:
          it?.dataJSON?.companyName ||
          it?.dataJSON?.whichService ||
          it?.type ||
          'İletişim Formu',
        createdDateTime: it?.createdDateTime ?? null,
        status: it?.status ?? '-',
        sourceType: 'Web Form',
        __raw: { ...it, _isContact: true }
      }));


    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const sourceTypeColumn = React.useMemo(() => ({
    id: 'sourceType',
    label: 'Form Tipi',
    sortBy: 'sourceType',
    format: (val, row) => row?.sourceType ?? '-',
    sx: { minWidth: 140 }
  }), []);

  // --- EKLE: hepsini birlikte yükleyen orchestrator ---
  const loadAllApplications = async () => {
    setLoading(true);
    try {
      const [apps, contacts] = await Promise.all([
        fetchCampaignApplications(),
        fetchContactFormResults()
      ]);
      // tek seferde state'e bas -> tablo yarım yüklenmez
      setApplications([...apps, ...contacts]);

    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadAllApplications();
    getCampaignQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = (row) => {
    const raw = row?.__raw || null;
    setDetailRow(raw);
    setSelectedStatus(raw ? String(raw.status ?? '') : null);
    setDetailOpen(true);
  };
  const closeDetail = () => {
    setDetailOpen(false);
    setDetailRow(null);
  };

  const CONTACT_LABELS = {
    companyName: 'Firma Adı',
    authorizedPersonName: 'Yetkili Adı',
    authorizedPersonPhoneNumber: 'Yetkili Telefonu',
    email: 'E-Posta',
    businessType: 'İşletme Türü',
    businessOfficialName: 'Resmi Firma Adı',
    businessOfficialOwner: 'Resmi Firma Sahibi',
    city: 'İl',
    district: 'İlçe',
    neighborhood: 'Mahalle',
    address: 'Adres',
    source: 'Kaynak',
    hasOtherBranch: 'Başka Şube Var mı?',
    whichService: 'Hizmet Türü',
    workingHours: 'Çalışma Saatleri',
    hasEnoughSpace: 'Yeterli Alan Var mı?',
    hasComputer: 'Bilgisayar Var mı?',
    hasInternet: 'İnternet Var mı?',
    hasCamera: 'Kamera Var mı?',
    worksOnHolidays: 'Resmi Tatilde Çalışır mı?',
    hasOtherMembership: 'Diğer Üyelik Var mı?',
    directionAddress: 'Yol Tarifi',
  };

  const CONTACT_PREFERRED_ORDER = [
    'companyName',
    'whichService',
    'authorizedPersonName',
    'authorizedPersonPhoneNumber',
    'email',
    'businessType',
    'businessOfficialName',
    'businessOfficialOwner',
    'city',
    'district',
    'neighborhood',
    'address',
    'source',
    'hasOtherBranch',
    'hasEnoughSpace',
    'hasComputer',
    'hasInternet',
    'hasCamera',
    'worksOnHolidays',
    'hasOtherMembership',
    'workingHours',
    'directionAddress',
  ];

  const originalStatus = useMemo(
    () => String(detailRow?.status ?? ''),
    [detailRow]
  );
  const isDirty = String(selectedStatus ?? '') !== originalStatus;

  const handleUpdateStatus = async () => {
    if (!detailRow || !selectedStatus) return;

    try {
      setUpdating(true);

      // Hem kampanya formu hem de iletişim formu için id güvenli yakalama
      const reqId = detailRow?.requestID ?? detailRow?.id;
      const isContact = !!detailRow?._isContact;

      console.log(reqId, isContact)
      // Doğru servisi seç
      const updateFn = isContact
        ? Services.ContactFormResultChangeStatus
        : Services.FormResultChangeStatus;

      // Backend’e gönder
      let res = await updateFn(parsedUser.token, reqId, Number(selectedStatus));
      // Listeyi optimistic şekilde güncelle
      loadAllApplications();

      // Detay state’ini güncelle ve modalı kapat
      setDetailRow(prev => ({ ...(prev || {}), status: String(selectedStatus) }));
      setDetailOpen(false);
    } catch (e) {
      console.error(e);
      // İstersen burada toaster vs. tetikleyebilirsin
    } finally {
      setUpdating(false);
    }
  };


  // ========== Yardımcılar: Etiket Normalizasyonu ve Cevap Çekme ==========
  const normalizeLabel = (str) => {
    if (!str) return '';
    const map = {
      'İ': 'i', 'I': 'i', 'Ş': 's', 'ş': 's', 'Ğ': 'g', 'ğ': 'g',
      'Ü': 'u', 'ü': 'u', 'Ö': 'o', 'ö': 'o', 'Ç': 'c', 'ç': 'c'
    };
    return String(str)
      .trim()
      .toLowerCase('tr')
      .replace(/[İIŞşĞğÜüÖöÇç]/g, (m) => map[m] || m)
      .replace(/\s+/g, ' ')
      .replace(/\s/g, '');
  };

  // Sorulardan label -> questionID[] indexi
  const labelIndex = useMemo(() => {
    const idx = {};
    for (const q of questions) {
      const lab = q?.content?.label;
      const key = normalizeLabel(lab);
      if (!key) continue;
      if (!idx[key]) idx[key] = [];
      idx[key].push(String(q.questionID));
    }
    return idx;
  }, [questions]);

  // belirli questionID'nin değerini oku
  const getAnswerByQid = useCallback((rowRaw, qid) => {
    if (!rowRaw?.dataJSON) return '-';
    const answersObj = rowRaw.dataJSON?.answers && typeof rowRaw.dataJSON.answers === 'object'
      ? rowRaw.dataJSON.answers
      : rowRaw.dataJSON;
    const v = answersObj?.[String(qid)];
    return v === undefined || v === null ? '-' : v;
  }, []);

  // Label aday listesine göre tek bir cevap değeri döndür (opsiyonel kolonlar için)
  const getAnswerByLabels = useCallback((rowRaw, labelCandidates, fallbackKeys = []) => {
    if (!rowRaw?.dataJSON) return '-';

    const answersObj = rowRaw.dataJSON?.answers && typeof rowRaw.dataJSON.answers === 'object'
      ? rowRaw.dataJSON.answers
      : rowRaw.dataJSON;

    const readValByQid = (qid) => {
      if (answersObj == null) return undefined;
      return answersObj[qid];
    };

    for (const lab of labelCandidates) {
      const key = normalizeLabel(lab);
      const qids = labelIndex[key] || [];
      for (const qid of qids) {
        const v = readValByQid(qid);
        if (v !== undefined && v !== null) {
          return v;
        }
      }
    }

    for (const k of fallbackKeys) {
      const v = answersObj?.[k];
      if (v !== undefined && v !== null) {
        return v;
      }
    }

    return '-';
  }, [labelIndex]);

  // Görüntüleme için değeri string'e çevir
  const toDisplay = (val) => {
    if (val === '-' || val === undefined || val === null) return '-';
    if (Array.isArray(val)) return val.join(', ');
    if (typeof val === 'object') {
      const city = val.cityName || val.cityCode;
      const town = val.townName || val.townCode;
      if (city || town) return [city, town].filter(Boolean).join(' / ');
      return JSON.stringify(val);
    }
    return String(val);
  };

  const isTruthyLike = (v) => {
    const s = String(v ?? '').trim().toLowerCase();
    return ['evet', 'true', '1', 'on', 'yes'].includes(s);
  };

  // Detay modalı için doldurulan alanlar
  const questionDict = useMemo(() => {
    const dict = {};
    questions.forEach(q => { dict[String(q.questionID)] = q.content; });
    return dict;
  }, [questions]);

  const filledQuestions = useMemo(() => {
    // Web Form (contact) yolu
    if (detailRow?._isContact && detailRow?.dataJSON && !detailRow?.dataJSON?.answers) {
      const ans = detailRow.dataJSON;
      // Gösterilmeyecek teknik alanları ayıkla (gerekirse ekle/çıkar)
      const EXCLUDE = new Set(['campaignTitle']); // burada yok ama koruyalım

      // Önce tercih edilen sıralamaya göre, sonra kalan anahtarlar alfabetik
      const keys = Object.keys(ans).filter((k) => !EXCLUDE.has(k));
      const preferred = CONTACT_PREFERRED_ORDER.filter((k) => keys.includes(k));
      const rest = keys.filter((k) => !preferred.includes(k)).sort((a, b) => a.localeCompare(b));

      const orderedKeys = [...preferred, ...rest];

      return orderedKeys
        .map((k) => {
          const value = ans[k];
          // Boş/önemsiz değerleri at
          if (
            value === undefined ||
            value === null ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'string' && value.trim() === '')
          ) {
            return null;
          }

          const label = CONTACT_LABELS[k] || k;

          // Basit tip tayini: adres/çalışma saatleri -> long, boolean benzeri -> yesno, diğer -> short
          let type = 'short';
          if (k === 'address' || k === 'workingHours' || k === 'directionAddress') type = 'long';
          else if (typeof value === 'boolean' || ['evet', 'hayır', 'hayir', 'yes', 'no', 'true', 'false', '1', '0'].includes(String(value).toLowerCase())) {
            type = 'yesno';
          }

          return {
            questionID: k,
            content: { label, type, options: [], labels: { yesLabel: 'Evet', noLabel: 'Hayır' } },
            value,
          };
        })
        .filter(Boolean);
    }

    // Kampanya formu (mevcut yol)
    if (!detailRow?.dataJSON) return [];
    const ans = detailRow.dataJSON.answers ? detailRow.dataJSON.answers : detailRow.dataJSON;

    const pairs = Object.entries(ans)
      .filter(([qid, v]) => {
        if (qid === 'campaignTitle') return false;
        return v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0);
      })
      .map(([qid, value]) => {
        const content = questionDict[String(qid)] || { label: qid, type: 'short', options: [], labels: {} };
        return { questionID: qid, content, value };
      });

    const orderIndex = {};
    questions.forEach((q, idx) => { orderIndex[String(q.questionID)] = idx; });
    return pairs.sort((a, b) => (orderIndex[a.questionID] ?? 99999) - (orderIndex[b.questionID] ?? 99999));
  }, [detailRow, questionDict, questions]);

  // Yalnız görüntüleme/disabled render
  const renderReadonlyField = useCallback((q) => {
    const { type, options = [], labels = {} } = q.content || {};
    const v = q.value;

    if (type === 'yesno') {
      const checked = isTruthyLike(v);
      const yesLabel = labels?.yesLabel || 'Evet';
      const noLabel = labels?.noLabel || 'Hayır';
      return (
        <Box mt={0.5}>
          <FormControlLabel control={<Switch checked={checked} disabled />} label={checked ? yesLabel : noLabel} />
        </Box>
      );
    }

    if (q.questionID === 'workingHours' && v && typeof v === 'object') {
      const days = Object.entries(v);
      return (
        <Box mt={0.5} display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={1}>
          {days.map(([day, info]) => {
            const open = info?.open ? 'Açık' : 'Kapalı';
            const range = info?.open ? `${info?.start ?? '-'} - ${info?.end ?? '-'}` : '';
            const trDay = DAY_TR[String(day).toLowerCase()] ?? String(day).toLowerCase(); // 🔥 Türkçeleştir
            return (
              <Card key={day} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{trDay}</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {open}{range ? ` • ${range}` : ''}
                </Typography>
              </Card>
            );
          })}
        </Box>
      );
    }

    // mevcut türler (short/long/multiselect/single/dropdown/citydistrict/diğer) aynı:
    if (type === 'short') {
      return <TextField fullWidth value={String(v ?? '')} disabled placeholder="-" />;
    }
    if (type === 'long') {
      return <TextField fullWidth multiline minRows={4} value={String(v ?? '')} disabled placeholder="-" />;
    }
    if (type === 'multiselect') {
      const arr = Array.isArray(v) ? v : [];
      return (
        <Box mt={0.5} display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={0.75}>
          {options.map(opt => {
            const checked = arr.includes(opt);
            return (
              <Card key={opt} variant="outlined" sx={{
                p: 1, borderRadius: 2,
                borderColor: checked ? 'primary.main' : 'divider',
                bgcolor: checked ? 'action.selected' : 'background.paper',
              }}>
                <FormControlLabel control={<Checkbox checked={checked} disabled />} label={opt} />
              </Card>
            );
          })}
        </Box>
      );
    }
    if (type === 'single') {
      const val = String(v ?? '');
      return (
        <RadioGroup value={val} onChange={() => { /* no-op */ }}>
          <Box mt={0.5} display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={0.75}>
            {options.map(opt => (
              <Card key={opt} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                <FormControlLabel control={<Radio checked={val === opt} disabled />} value={opt} label={opt} />
              </Card>
            ))}
          </Box>
        </RadioGroup>
      );
    }
    if (type === 'dropdown') {
      const val = String(v ?? '');
      return (
        <FormControl fullWidth>
          <Select value={val} displayEmpty disabled>
            <MenuItem value="" disabled>Seçim yapın</MenuItem>
            {options.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    if (type === 'citydistrict') {
      const cityName = v?.cityName || v?.cityCode || '';
      const townName = v?.townName || v?.townCode || '';
      return (
        <Box display="flex" gap={1.5} width="100%">
          <TextField fullWidth value={cityName} disabled label="İl" />
          <TextField fullWidth value={townName} disabled label="İlçe" />
        </Box>
      );
    }
    return <TextField fullWidth value={typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')} disabled />;
  }, []);

  const pickFullName = useCallback(
    (rowRaw) => {
      let v = getAnswerByLabels(
        rowRaw,
        ['Ad Soyad', 'Adı Soyadı', 'Ad Soyadınız', 'İsim Soyisim', 'İsim ve Soyisim', 'Full Name'],
        [
          'fullName',
          'fullname',
          'name',
          'adSoyad',
          // web form özel:
          'authorizedPersonName',
          'businessOfficialOwner',
        ]
      );

      const toClean = (s) => (typeof s === 'string' ? s.trim() : '');

      if (v && typeof v === 'object') {
        const fn = toClean(v.firstName || v.givenName || v.name || '');
        const ln = toClean(v.lastName || v.surname || v.familyName || '');
        const full = [fn, ln].filter(Boolean).join(' ').trim();
        if (full) return full;
      } else if (typeof v === 'string' && toClean(v)) {
        return toClean(v);
      }

      const first = getAnswerByLabels(
        rowRaw,
        ['Ad', 'Adınız', 'İsim', 'First Name', 'Given Name'],
        ['firstName', 'givenName', 'adi']
      );
      const last = getAnswerByLabels(
        rowRaw,
        ['Soyad', 'Soyadınız', 'Soyisim', 'Last Name', 'Surname', 'Family Name'],
        ['lastName', 'surname', 'familyName', 'soyadi']
      );

      const fn = toClean(typeof first === 'object' ? (first.firstName || first.name || first.givenName) : first);
      const ln = toClean(typeof last === 'object' ? (last.lastName || last.surname || last.familyName) : last);

      if (fn || ln) return [fn, ln].filter(Boolean).join(' ').trim();

      const answersObj = rowRaw?.dataJSON?.answers ?? rowRaw?.dataJSON ?? {};
      const possibleFull = toClean(
        answersObj.fullName || answersObj.fullname || answersObj.name || answersObj.adSoyad || ''
      );
      if (possibleFull) return possibleFull;

      return '-';
    },
    [getAnswerByLabels]
  );

  const pickPhone = useCallback(
    (rowRaw) => {
      const v = getAnswerByLabels(
        rowRaw,
        ['Telefon', 'Telefon Numarası', 'GSM', 'Cep Telefonu', 'Phone', 'Phone Number', 'Telefon No'],
        [
          'phone',
          'tel',
          'gsm',
          'mobile',
          'phoneNumber',
          // web form özel:
          'authorizedPersonPhoneNumber',
        ]
      );
      if (v === null || v === undefined) return '-';
      if (typeof v === 'object') return (v.value && String(v.value).trim()) || '-';
      const s = String(v).trim();
      return s || '-';
    },
    [getAnswerByLabels]
  );

  const pickEmail = useCallback(
    (rowRaw) => {
      const v = getAnswerByLabels(
        rowRaw,
        ['E-posta', 'E-Posta', 'Email', 'E mail', 'Mail'],
        ['email', 'mail']
      );
      if (v === null || v === undefined) return '-';
      if (typeof v === 'object') return (v.value && String(v.value).trim()) || '-';
      const s = String(v).trim();
      return s || '-';
    },
    [getAnswerByLabels]
  );

  /** ======================= TABLO KOLONLARI ======================= */
  const staticColumns = useMemo(() => ([
    {
      id: 'campaignTitle',
      label: 'Form İçeriği',
      // Web Form ise whichService göster, değilse mevcut campaignTitle
      format: (_val, row) => {
        const isWebForm = row?.sourceType === 'Web Form' || row?.__raw?._isContact;
        const whichService = row?.__raw?.dataJSON?.whichService;
        return isWebForm ? (whichService ?? row?.campaignTitle ?? '-') : (row?.campaignTitle ?? '-');
      },
      // Sıralama da görüntülenen değere göre yapılsın
      sortFunction: (row) => {
        const isWebForm = row?.sourceType === 'Web Form' || row?.__raw?._isContact;
        const whichService = row?.__raw?.dataJSON?.whichService;
        return String(isWebForm ? (whichService ?? row?.campaignTitle ?? '') : (row?.campaignTitle ?? ''));
      },
      sx: { minWidth: 240 }
    },
    // ✅ Yeni: Başvuran Kişi Adı (Ad + Soyad birleştirilmiş)
    {
      id: 'applicantName',
      label: 'Başvuran Kişi Adı',
      sortFunction: (row) => pickFullName(row?.__raw),
      format: (_val, row) => pickFullName(row?.__raw),
      sx: { minWidth: 220 }
    },
    // ✅ Yeni: Telefon
    {
      id: 'phone',
      label: 'Telefon',
      sortFunction: (row) => pickPhone(row?.__raw),
      format: (_val, row) => pickPhone(row?.__raw),
      sx: { minWidth: 160 }
    },
    // ✅ Yeni: E-Posta
    {
      id: 'email',
      label: 'E-Posta',
      sortFunction: (row) => pickEmail(row?.__raw),
      format: (_val, row) => pickEmail(row?.__raw),
      sx: { minWidth: 220 }
    },
    {
      id: 'createdDateTime',
      label: 'Oluşturulma Tarihi',
      sortFunction: (row) => Number(row?.createdDateTime || 0),
      format: (val) => formatDate(val),
      sx: { minWidth: 180 }
    },
    {
      id: 'status',
      label: 'Durum',
      sortBy: 'status',
      format: (val) => {
        const statusObj = campaignStatusList.find((s) => String(s.status) === String(val));
        if (!statusObj) return '-';
        return (
          <Chip
            label={statusObj.label}
            sx={{ backgroundColor: statusObj.color, color: '#fff', fontWeight: 600, borderRadius: '6px' }}
            size="small"
          />
        );
      },
      sx: { minWidth: 140 }
    }
  ]), [pickFullName, pickPhone, pickEmail]);

  // 2) Soru bazlı dinamik kolonlar
  const questionColumns = useMemo(() => {
    return questions.map((q) => {
      const id = `q_${q.questionID}`;
      const label = q?.content?.label || `Soru ${q.questionID}`;
      return {
        id,
        label,
        sortFunction: (row) => {
          const raw = row?.__raw;
          const v = getAnswerByQid(raw, q.questionID);
          return toDisplay(v);
        },
        format: (_val, row) => {
          const raw = row?.__raw;
          const v = getAnswerByQid(raw, q.questionID);
          return toDisplay(v);
        },
        sx: { minWidth: 200 }
      };
    });
  }, [questions, getAnswerByQid]);

  // 3) Aksiyon (Detay) kolonu - sabit ve en sonda
  const actionsColumn = useMemo(() => ({
    id: 'actions',
    label: 'Detay',
    format: (_val, row) => (
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Iconify icon="icon-park-outline:message-search" style={{ cursor: 'pointer' }} color="#C1C4C9" onClick={() => openDetail(row)} />
      </div>
      // <Button variant="contained" size="small" onClick={() => openDetail(row)}>
      //   Detay
      // </Button>
    ),
    sx: { width: 120, textAlign: 'center' }
  }), []);

  // 4) Tüm kolon havuzu (actions hariç)
  const allColumnsPool = useMemo(() => {
    return [...staticColumns, ...questionColumns];
  }, [staticColumns, questionColumns]);

  // 5) ID -> kolon map
  const allMap = useMemo(() => {
    const m = new Map();
    allColumnsPool.forEach(c => m.set(c.id, c));
    return m;
  }, [allColumnsPool]);

  // 6) Tabloya gidecek headers (görünür sıralama + actions sonda)
  const headers = useMemo(() => {
    const arr = [];
    visibleColumnIds.forEach(id => {
      const col = allMap.get(id);
      if (col) arr.push(col);
    });

    const idx = arr.findIndex(c => c.id === 'campaignTitle');
    if (idx >= 0) {
      // Form Tipi, Form İçeriği'nden önce görünsün
      arr.splice(idx, 0, sourceTypeColumn);
    } else {
      arr.unshift(sourceTypeColumn);
    }

    arr.push(actionsColumn); // sabit & sondadır
    return arr;
  }, [visibleColumnIds, allMap, actionsColumn, sourceTypeColumn]);

  const serializeCell = useCallback(
    (col, row) => {
      // Statü metni
      if (col.id === 'status') {
        const statusObj = campaignStatusList.find(
          (s) => String(s.status) === String(row?.status)
        );
        return statusObj?.label ?? '-';
      }

      // Kolon özel format fonksiyonu varsa kullan
      if (typeof col.format === 'function') {
        const out = col.format(row?.[col.id], row);
        if (typeof out === 'string') return out;
        // Chip vb. React öğesi dönerse label/children yakalamayı dene
        if (out && typeof out === 'object') {
          if (out.props?.label) return String(out.props.label);
          if (out.props?.children) return String(out.props.children);
        }
        return '-';
      }

      // Düz değer
      const v = row?.[col.id];
      if (v === undefined || v === null) return '-';
      return typeof v === 'object' ? JSON.stringify(v) : String(v);
    },
    []
  );

  const buildExportPayload = useCallback(() => {
    // actions hariç mevcut görünen kolonlar
    const exportableCols = headers.filter((h) => h.id !== 'actions');

    // İçerik
    const content = applications.map((row) => {
      const obj = {};
      exportableCols.forEach((col) => {
        obj[col.label] = serializeCell(col, row);
      });
      return obj;
    });

    // Header tipleri
    const header = {};
    exportableCols.forEach((col) => {
      header[col.label] = 'string';
    });

    return {
      type: 'excel',
      content,
      header,
      sheetTitle: 'Kampanya Başvuruları',
    };
  }, [applications, headers, serializeCell]);

  const exportToExcel = useCallback(() => {
    const payload = buildExportPayload();
    api
      .post('excel/export', payload)
      .then((response) => {
        if (response?.data?.status) {
          const w = window.open(response.data.result, '_blank');
          if (!w || w.closed || typeof w.closed === 'undefined') {
            setShowPopUpError(true);
            throw new Error('Pop-up engellendi');
          }
        } else {
          setShowPopUpError(true);
        }
      })
      .catch((err) => {
        console.error(err);
        setShowPopUpError(true);
      });
  }, [buildExportPayload]);
  /** ======================= RENDER ======================= */
  return (
    <div>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Kampanya Form Başvuruları
      </Typography>

      <div style={{ width: '100%', alignItems: 'center', justifyContent: 'flex-end', display: 'flex' }}>
        <Button sx={{ marginRight: '15px' }} variant="outlined" onClick={() => setColumnEditorOpen(true)}>
          Tablo Düzenle
        </Button>

        <ExportToExcelButton
          showPopUpError={showPopUpError}
          setShowPopUpError={setShowPopUpError}
          onClick={exportToExcel}
        />
      </div>

      {loading ?

        <div style={{ width: '100%', height: '100%', marginTop: '350px', alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
          <CircularProgress size={22} color="error" />
        </div>
        :
        <DynamicTable
          data={applications}
          headers={headers}
          rowsPerPageOptions={[25, 50, 100]}
          initialRowsPerPage={25}
          initialOrder="createdDateTime"
          initalOrderDirection="desc"
          isOrdered
        />
      }

      {/* Kolon Düzenleme Modalı */}
      <ColumnEditorModal
        open={columnEditorOpen}
        onClose={() => setColumnEditorOpen(false)}
        allColumns={allColumnsPool.map(({ id, label }) => ({ id, label }))}
        visibleOrder={visibleColumnIds}
        onApply={(newOrder) => setVisibleColumnIds(newOrder)}
      />

      {/* Detay Modal - Readonly Form Görünümü */}
      <Modal open={detailOpen} onClose={closeDetail}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 900 },
            bgcolor: 'white',
            borderRadius: 2, boxShadow: 24, p: 3,
            maxHeight: '85vh', overflow: 'auto'
          }}
        >
          <Box position="relative" mb={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 800, textAlign: 'center' }}>
              Talep Detayları
            </Typography>
            <IconButton
              onClick={closeDetail}
              aria-label="Kapat"
              sx={{ position: 'absolute', top: -4, right: -4 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#6B7280' }}>
              {detailRow?.dataJSON?.campaignTitle || detailRow?.dataJSON?.companyName || 'Form'}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" sx={{ mb: 2 }} gap={"8px"}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Form ID: {detailRow?.requestID || detailRow?.id}
            </Typography>

            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Durum:
              </Typography>
              {(() => {
                const statusObj = campaignStatusList.find(
                  (s) => String(s.status) === String(detailRow?.status)
                );
                if (!statusObj) return null;
                return (
                  <Chip
                    label={statusObj.label}
                    size="small"
                    sx={{
                      backgroundColor: statusObj.color,
                      color: '#fff',
                      fontWeight: 600,
                      borderRadius: '6px'
                    }}
                  />
                );
              })()}
            </Box>
          </Box>

          {filledQuestions.length === 0 && (
            <QuestionCard>
              <Typography variant="body2" color="text.secondary">Gösterilecek yanıt bulunamadı.</Typography>
            </QuestionCard>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 280px' },
              gap: 2,
            }}
          >
            <Box>
              {filledQuestions.length === 0 && (
                <QuestionCard>
                  <Typography variant="body2" color="text.secondary">
                    Gösterilecek yanıt bulunamadı.
                  </Typography>
                </QuestionCard>
              )}

              {filledQuestions.map((q) => (
                <Box key={q.questionID} mb={2.25}>
                  <QuestionCard>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: '#667085', fontSize: '12px' }}
                      >
                        {q.content?.label || q.questionID}
                      </Typography>
                    </Box>
                    {renderReadonlyField(q)}
                  </QuestionCard>
                  <Divider sx={{ mt: 1.5 }} />
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                borderRadius: "8px",
                border: '1px solid #EFEFEF',
                p: 2,
                height: 'fit-content',
                position: { md: 'sticky' },
                top: { md: 16 },
                mt: 3,
                alignSelf: 'start',
                bgcolor: '#F9F9F9'
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#667085', mb: 1 }}>Durumlar</Typography>
              <Stack spacing={1.25}>
                {campaignStatusList.map((s) => {
                  const checked = String(selectedStatus ?? '') === String(s.status);
                  return (
                    <Box key={s.status} display="flex" alignItems="center" gap={1}>
                      <Checkbox checked={checked} onChange={() => setSelectedStatus(String(s.status))} />
                      <Chip
                        label={s.label}
                        size="small"
                        sx={{
                          backgroundColor: s.color,
                          color: '#fff',
                          fontWeight: 600,
                          borderRadius: "2px",
                          height: '40px',
                          width: '100%'
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Box>

          <Box mt={3} display="flex" justifyContent="center" gap={2}>
            <Button variant="outlined" onClick={closeDetail} disabled={updating}>
              Vazgeç
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdateStatus}
              disabled={updating || !isDirty || !selectedStatus}
            >
              {updating ? 'Güncelleniyor...' : 'Güncelle'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}